import { useCallback, useMemo } from "react";
import type { EventStatistics, EventTopConsumer } from "../../services/drinkService";
import { buildTimelineSeries, filterVisibleParticipants, selectDrinkStats, selectTopEntry } from "./eventStats/utils";
import { buildVisibleStatistics } from "./eventStats/derived";

type Props = {
    stats: EventStatistics | null;
    selectedDrinkId: number | null;
    visibleUserSet: Set<number>;
    showAllParticipants: boolean;
};

export const useEventStatsDerived = ({ stats, selectedDrinkId, visibleUserSet, showAllParticipants }: Props) => {
    const filterParticipantsByVisibility = useCallback((participants: EventTopConsumer[] = []) =>
        filterVisibleParticipants(participants, showAllParticipants, visibleUserSet),
    [showAllParticipants, visibleUserSet]);
    const visibleDrinks = useMemo(() => stats ? buildVisibleStatistics(stats.drinks, filterParticipantsByVisibility) : [], [stats, filterParticipantsByVisibility]);
    const visibleCategories = useMemo(() => stats ? buildVisibleStatistics(stats.categories, filterParticipantsByVisibility) : [], [stats, filterParticipantsByVisibility]);
    const selectedDrinkStats = useMemo(() => selectDrinkStats(selectedDrinkId, visibleDrinks), [selectedDrinkId, visibleDrinks]);
    const filteredUsers = useMemo(() => stats?.users.filter((participant) =>
        (showAllParticipants || visibleUserSet.has(participant.userId)) &&
        (!selectedDrinkStats || selectedDrinkStats.participantMap.has(participant.userId))) ?? [],
    [stats, visibleUserSet, selectedDrinkStats, showAllParticipants]);
    const filteredTotals = useMemo(() => ({
        totalVolume: filteredUsers.reduce((sum, participant) => sum + participant.totalVolumeMl, 0),
        totalAlcohol: filteredUsers.reduce((sum, participant) => sum + participant.totalAlcoholGrams, 0),
        totalEntries: filteredUsers.reduce((sum, participant) => sum + participant.activityCount, 0),
    }), [filteredUsers]);
    const topAlcoholUser = useMemo(() => [...filteredUsers]
        .sort((a, b) => b.totalAlcoholGrams - a.totalAlcoholGrams || b.totalVolumeMl - a.totalVolumeMl)
        .slice(0, 5), [filteredUsers]);
    const timelineSeries = useMemo(() => buildTimelineSeries(stats?.timeline ?? [], showAllParticipants ? undefined : visibleUserSet), [stats, visibleUserSet, showAllParticipants]);
    const topBeerEntry = useMemo(() => {
        const keywords = ["beer", "bier", "lager", "pils", "ipa"];
        return selectTopEntry(visibleDrinks, visibleCategories, (name) => keywords.some((keyword) => name.includes(keyword)), true)
            ?? selectTopEntry(visibleDrinks, visibleCategories, () => true, false);
    }, [visibleDrinks, visibleCategories]);
    const topShotsEntry = useMemo(() => {
        const keywords = ["shot", "spirit", "sprit", "schnaps", "vodka", "rum", "whisky", "whiskey", "gin", "tequila", "liqueur"];
        const match = selectTopEntry(visibleDrinks, visibleCategories, (name) => keywords.some((keyword) => name.includes(keyword)), true);
        const drinks = topBeerEntry ? visibleDrinks.filter((drink) => drink.drinkName !== topBeerEntry.label) : visibleDrinks;
        const categories = topBeerEntry ? visibleCategories.filter((category) => category.category !== topBeerEntry.label) : visibleCategories;
        return match ?? selectTopEntry(drinks, categories, () => true, false);
    }, [visibleDrinks, visibleCategories, topBeerEntry]);
    const selectedDrinkLeaderboard = useMemo(() => selectedDrinkStats ? ({
        drink: selectedDrinkStats.drink,
        participants: selectedDrinkStats.drink.participants,
        participantMap: selectedDrinkStats.participantMap,
    }) : null, [selectedDrinkStats]);
    const drinkOptions = useMemo(() => visibleDrinks.map((drink) => ({ value: drink.drinkId, label: drink.drinkName })), [visibleDrinks]);
    const participantOptions = useMemo(() => stats?.users.map((participant) => ({ value: participant.userId, label: participant.username })) ?? [], [stats]);

    return { visibleDrinks, visibleCategories, selectedDrinkStats, filteredUsers, filteredTotals,
        topAlcoholUser, timelineSeries, topBeerEntry, topShotsEntry, selectedDrinkLeaderboard,
        drinkOptions, participantOptions };
};
